import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { DeltaBadge } from '@/components/performance/DeltaBadge';
import { EngagementStats } from '@/components/performance/EngagementStats';
import { WinnerBadge } from '@/components/performance/WinnerBadge';
import { PERFORMANCE_POLL_MS } from '@/constants/performance';
import { api } from '@/lib/api';
import { formatRelative, shortId } from '@/lib/format';
import type { EngagementSnapshot } from '@/types/engagement';

interface PerformanceDetailPageProps {
  contentId: string;
}

export function PerformanceDetailPage({ contentId }: PerformanceDetailPageProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['performance', contentId],
    queryFn: () => api.fetchContentPerformance(contentId),
    refetchInterval: PERFORMANCE_POLL_MS,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <BackLink />
        <Skeleton className="h-24 w-full rounded-lg" />
        <Skeleton className="h-48 w-full rounded-lg" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-4">
        <BackLink />
        <p className="text-sm text-muted-foreground">Content not found.</p>
      </div>
    );
  }

  const snap = data.latest_snapshot;

  return (
    <div className="space-y-6">
      <BackLink />

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-mono text-sm font-semibold">{shortId(contentId)}</h1>
            <WinnerBadge isWinner={data.is_winner} />
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Persona: <span className="font-mono">{shortId(data.persona_id)}</span>
            {data.posted_at && (
              <> · Posted {formatRelative(data.posted_at)}</>
            )}
          </p>
        </div>
        {data.post_url && (
          <a href={data.post_url} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm">
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              View on Instagram
            </Button>
          </a>
        )}
      </div>

      {/* Latest metrics */}
      <div className="rounded-lg border border-border bg-card p-4">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Latest metrics
          {snap && (
            <span className="ml-2 font-normal normal-case">
              scraped {formatRelative(snap.scraped_at)}
            </span>
          )}
        </h2>
        {snap ? (
          <EngagementStats snapshot={snap} className="text-base gap-6" />
        ) : (
          <p className="text-sm text-muted-foreground">
            No data yet — scrape fires 24 hours after posting.
          </p>
        )}
      </div>

      {/* Snapshot history */}
      {data.snapshot_count > 0 && snap && (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="border-b border-border px-4 py-2">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Snapshot history ({data.snapshot_count})
            </h2>
          </div>
          {/* We only have the latest snapshot from this endpoint in M2.
              The detail page shows the current snapshot; full history needs
              a GET /performance/{id}/snapshots endpoint (M3+). */}
          <SnapshotRow snap={snap} previous={null} />
        </div>
      )}
    </div>
  );
}

function SnapshotRow({
  snap,
  previous,
}: {
  snap: EngagementSnapshot;
  previous: EngagementSnapshot | null;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-border last:border-0">
      <div className="space-y-1">
        <EngagementStats snapshot={snap} />
        {previous && (
          <div className="flex items-center gap-2">
            <DeltaBadge
              current={snap.views}
              previous={previous.views}
              label="views"
            />
            <DeltaBadge
              current={snap.likes}
              previous={previous.likes}
              label="likes"
            />
          </div>
        )}
      </div>
      <span className="text-xs text-muted-foreground">{formatRelative(snap.scraped_at)}</span>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/performance"
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-3.5 w-3.5" />
      Performance
    </Link>
  );
}
